import { ResizeObserver } from '@juggle/resize-observer';
import { EventEmitter } from 'events';
import axios from 'axios';
import ShelfPack from '@mapbox/shelf-pack';

import KeyCodes from './keycodes';
import Interface from './interface';
import Mode from './mode';

import Rect from './rect';
import Part from './part';
import Bin from './bin';
import Handler from './handler';

import TransformOperation from './operations/transform_operation';
import RemoveOperation from './operations/remove_operation';
import CopyOperation from './operations/copy_operation';

import { getClearanceFromThickness } from '@/plugins/emarf';

const minScale = 0.06;
const maxScale = 2.0;
const interval = 50;
const offset = 1500;

const rowMax = 5;
let oneRowMove = 0;

export default class Nesting extends EventEmitter {

  constructor (dom) {
    super();

    this.source = {};
    
    // geometries
    this.bins = [];
    this.parts = [];
    this.thickness = 0;
    this.row = 0;
    this.initialBinY = 0;
    
    this.previousMoveWasMinusBin = false;
    this.largestHistoryRow = 0;
    
    this.selectedNodes = [];
    this.selectedRect = null;
    
    const clearance = getClearanceFromThickness(24);
    this.binClearance = clearance.bin;
    this.partClearance = clearance.part;

    // ui
    this.events = [];
    this.position = new THREE.Vector2();
    this.scale = 1.0;
    this.start = new THREE.Vector2();
    this.prev = new THREE.Vector2();
    this.button = -1;
    this.keyCode = -1;
    this.keys = {};

    this.current = 0;
    this.history = [];

    // dom
    this.root = dom;
    this.rect = this.root.getBoundingClientRect();

    this.view = this.createView();
    this.grid = this.createGrid();
    this.view.appendChild(this.grid);
    this.root.appendChild(this.view);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    // svg.setAttribute('width', 6000)
    // svg.setAttribute('height', 6000)
    this.svg = svg;
    this.view.appendChild(svg);

    this.setupEvent(dom);

    this.move(0, 0);
    // this.debug().then(this.reset.bind(this))

    /*
    this.rotationLabel = document.createElement('span')
    this.rotationLabel.classList.add('rotation-label')
    this.appendElement(this.rotationLabel)
    */

    this.handler = new Handler();
    this.svg.appendChild(this.handler.element);
  }

  simplify (vertices, delta = 1e-2) {
    const scale = 1e6;
    const inv = 1 / scale;

    const path = ClipperLib.Clipper.CleanPolygon(vertices.map(v => {
      return {
        X: v.x * scale,
        Y: v.y * scale
      };
    }), delta * scale);

    const simplified = path.map(v => {
      return {
        x: v.X * inv,
        y: v.Y * inv
      };
    });

    if (simplified.length <= 0) return vertices;

    // return vertices
    // console.log(vertices.length, simplified.length)
    return simplified;
  }

  setupEvent (el) {
    this.addEvent(el, 'contextmenu', (e) => {
      e.preventDefault();
    });

    this.addEvent(el, 'wheel', (e) => {
      let r = this.root.getBoundingClientRect();
      let p = Interface.getMousePosition(e);
      let x = p.x - r.x;
      let y = p.y - r.y;
      let delta = -e.deltaY * 0.001;
      this.zoom(delta, x, y, r.width, r.height);
      e.preventDefault();
    });

    this.addEvent(el, 'mousedown', this.onMouseDown.bind(this), false);
    this.addEvent(el, 'mousemove', this.onMouseMove.bind(this), false);
    this.addEvent(el, 'mouseup', this.onMouseUp.bind(this), false);
    this.addEvent(window, 'keydown', this.onKeyDown.bind(this), false);
    this.addEvent(window, 'keyup', this.onKeyUp.bind(this), false);

    // new ResizeObserver(() => { this.resize() }).observe(el)
  }

  appendElement (dom) {
    this.root.appendChild(dom);
    this.move(0, 0); // to fix a bug in Chrome
  }

  createView () {
    let view = document.createElement('div');
    view.classList.add('view');
    view.style.transformOrigin = '0px 0px 0px';
    view.addEventListener('mousedown', (e) => { }, false);
    view.addEventListener('touchstart', (e) => { }, false);
    view.addEventListener('click', (e) => { }, false);
    view.addEventListener('dblclick', (e) => { }, false);
    return view;
  }

  createGrid () {
    let grid = document.createElement('div');
    grid.classList.add('grid');
    return grid;
  }

  convert (x, y) {
    return {
      x: (x - this.position.x) / this.scale,
      y: (y - this.position.y) / this.scale
    };
  }

  panable (button, keyCode) {
    return (button === 0 && (keyCode === KeyCodes.SHIFT)) || button === 1 || button === 2;
  }

  pan (dx, dy) {
    this.move(dx, dy);
  }

  setIsPanning (panning) {
    if (panning) {
      this.root.classList.add('panning');
    } else {
      this.root.classList.remove('panning');
    }
  }

  move (dx, dy) {
    this.moveTo(this.position.x + dx, this.position.y + dy);
    this.transform(this.position.x, this.position.y, this.scale);
  }

  moveTo (x, y) {
    this.position.x = x;
    this.position.y = y;
    this.transform(this.position.x, this.position.y, this.scale);
  }

  zoom (delta, x, y, w, h) {
    let ns = this.scale * (1 + delta);
    if (ns < minScale || maxScale < ns) return;

    // calculate a absolute position
    let ax = this.position.x / this.scale;
    let ay = this.position.y / this.scale;

    let rx = x / w;
    let ry = y / h;

    this.position.x = (ax * ns) - w * delta * rx;
    this.position.y = (ay * ns) - h * delta * ry;
    this.scale = ns;

    this.transform(this.position.x, this.position.y, this.scale);
  }

  transform (px, py, s) {
    this.view.style.transform = `translate(${px}px, ${py}px) scale(${s})`;

    const x = px / s;
    const y = py / s;
    this.grid.style.transform = `translate(${-x}px, ${-y}px)`;

    const percent = `${100 / s}%`;
    this.grid.style.width = this.grid.style.height = percent;
    this.grid.style.backgroundPosition = `${x}px ${y}px`;
    this.grid.style.opacity = Math.min(1, s * s);
  }

  beginMode (mode) {
    this.mode = mode;
    switch (mode) {
      case Mode.MULTIPLE_SELECT_NODE:
        this.removeSelectedRect();
        this.selectedRect = new Rect(this);
        this.svg.appendChild(this.selectedRect.element);
        break;
    }
  }

  resetMode () {
    const prev = this.mode;
    this.move(0, 0);
    this.beginMode(Mode.DEFAULT);
    this.unselectAll();
    if (prev !== Mode.DEFAULT) {
      this.updateIntersections();
    }
    this.handler.hide();
  }

  removeSelectedRect () {
    if (this.selectedRect !== null) {
      this.selectedRect.dispose();
      this.selectedRect = null;
    }
  }

  selectAll () {
    this.selectNodes(this.parts);
  }

  unselectAll () {
    this.unselectNodes();
  }

  selectNode (node) {
    if (!this.selectedNodes.includes(node)) {
      node.select();
      this.selectedNodes.push(node);
    }
  }

  selectNodes (nodes) {
    this.selectedNodes = nodes;
    this.selectedNodes.forEach(n => n.select());
    this.beginMode(Mode.SELECT_KEEP_NODE);
  }

  unselectNodes () {
    if (this.selectedNodes.length > 0) this.selectedNodes.forEach((node) => node.unselect());
    this.selectedNodes = [];
  }

  hasIntersectedPart () {
    return this.parts.some(p => p.intersected);
  }

  updateIntersections () {
    this.parts.forEach(p => p.unmarkIntersected());

    const bins = this.parts.map(p => {
      return this.bins.find(b => b.containsPart(p));
    });

    for (let i = 0, n = this.parts.length; i < n; i++) {
      const p0 = this.parts[i];
      const b0 = bins[i];
      if (b0 === undefined) {
        p0.markIntersected();
        continue;
      }

      for (let j = i + 1; j < n; j++) {
        const p1 = this.parts[j];
        const b1 = bins[j];

        if (b1 === undefined) {
          p1.markIntersected();
          continue;
        }

        if (
          (p0.intersected && p1.intersected) ||
          (b0 !== b1)
        ) {
          continue;
        }

        if (p0.intersects(p1)) {
          p0.markIntersected();
          p1.markIntersected();
        }
      }
    }
  }

  onModeEnded (e) {
    switch (this.mode) {
      case Mode.MULTIPLE_SELECT_NODE:
        let intersected = [];

        // check just a left click
        let area = this.selectedRect.area();
        if (area > Number.EPSILON) {
          intersected = this.parts.filter((node) => {
            return this.selectedRect.intersects(node);
          });
        }

        if (intersected.length > 0) {
          this.selectNodes(intersected);
        } else {
          this.resetMode();
        }

        this.removeSelectedRect();
        break;

      default:
        this.resetMode();
        break;
    }
  }

  onSelect (e, node) {
    switch (this.mode) {
      default:
        this.beginMode(Mode.SELECT_NODE);
        this.selectNode(node);
        this.selectedNodes.forEach((node) => node.prepareDrag(e));
        break;
    }
  }

  onRotate (part, pivot) {
    // console.log(part, pivot)
    let cx = Number(pivot.getAttribute('cx'));
    let cy = Number(pivot.getAttribute('cy'));
    this.handler.show();
    this.handler.move(cx, cy);

    this.unselectAll();
    this.selectNode(part);
    this.beginMode(Mode.ROTATE_NODE);
    part.prepareRotate();
  }

  rotate (x, y, snap) {
    let angle = -this.handler.rotate(x, y);
    if (snap) {
      const u = Math.PI * 0.25;
      angle = Math.ceil(angle / u) * u;
      this.selectedNodes.forEach(part => part.rotateTo(angle));
    } else {
      this.selectedNodes.forEach(part => part.rotate(angle));
    }
  }

  onMouseDown (e) {
    this.button = e.button;

    let r = this.root.getBoundingClientRect();
    let p = Interface.getMousePosition(e);
    let x = p.x - r.x;
    let y = p.y - r.y;

    this.start.x = this.prev.x = p.x;
    this.start.y = this.prev.y = p.y;

    let which = e.which;

    switch (this.mode) {
      case Mode.DEFAULT:
        switch (which) {
          case 0: // no button (0 touch)
          case 1: // left click
            switch (this.keyCode) {
              case KeyCodes.CTRL:
              case KeyCodes.COMMAND:
                break;
              case KeyCodes.SHIFT:
                break;
              default:
                this.beginMode(Mode.MULTIPLE_SELECT_NODE);
                break;
            }
            break;
        }
        break;
    }
  }

  onMouseMove (e) {
    let r = this.root.getBoundingClientRect();
    let p = Interface.getMousePosition(e);
    let x = p.x - r.x;
    let y = p.y - r.y;
    let wp = this.convert(x, y);

    let which = e.which;

    if (this.mode !== Mode.ROTATE_NODE && (this.panable(this.button, this.keyCode))) {
      this.pan(
        p.x - this.prev.x,
        p.y - this.prev.y
      );
      this.setIsPanning(true);

      // Reset drag points
      this.selectedNodes.forEach((node) => node.prepareDrag(e));
    }

    switch (this.mode) {
      case Mode.SELECT_NODE: // select to drag mode
      case Mode.DRAG_NODE:
        if (which === 1 && !this.panable(this.button, this.keyCode)) { // CAUTION: Panningとバッティングしないようにする条件
          this.selectedNodes.forEach((node) => node.drag(e, this));
          this.beginMode(Mode.DRAG_NODE);
        }
        break;

      case Mode.ROTATE_NODE:
        if (which === 1) {
          const snap = (this.keys[KeyCodes.CTRL] || this.keys[KeyCodes.COMMAND]);
          this.rotate(wp.x, wp.y, snap);
        }
        break;

      case Mode.MULTIPLE_SELECT_NODE:
        let sp = this.convert(this.start.x - r.x, this.start.y - r.y);
        this.selectedRect.update(sp.x, sp.y, wp.x, wp.y);
        break;
    }

    this.prev.x = p.x;
    this.prev.y = p.y;
  }

  onMouseUp (e) {
    let r = this.root.getBoundingClientRect();
    let p = Interface.getMousePosition(e);
    let x = p.x - r.x;
    let y = p.y - r.y;
    let wp = this.convert(x, y);

    let which = e.which;

    switch (this.mode) {
      case Mode.SELECT_NODE:
        break;

      case Mode.DRAG_NODE:
      case Mode.ROTATE_NODE:
        this.pushHistory(new TransformOperation(this.selectedNodes));
        this.onModeEnded(e);
        break;

      case Mode.MULTIPLE_SELECT_NODE:
        this.onModeEnded(e);
        break;

      case Mode.SELECT_KEEP_NODE:
        switch (which) {
          case 1: // left
            this.onModeEnded(e);
            break;
          default: // middle, right click
            break;
        }
        break;

      default:
        this.onModeEnded(e);
        break;
    }

    e.preventDefault();
    e.stopPropagation();
    this.button = -1;
    this.setIsPanning(false);
  }

  onKeyDown (e) {
    if (e.target !== document.body) return;

    this.trackKey(e.keyCode);

    if (this.keys[KeyCodes.CTRL] || this.keys[KeyCodes.COMMAND]) {
      if (this.keys[KeyCodes.A]) {
        this.selectAll();
      } else if (this.keys[KeyCodes.Y]) {
        this.redo();
      } else if (this.keys[KeyCodes.Z]) {
        this.undo();
      }
    }

    e.preventDefault();
  }

  onKeyUp (e) {
    const keyCode = e.keyCode;
    this.clearKey(keyCode);
    e.preventDefault();
  }

  addEvent (el, trigger, callback) {
    let evt = {
      el: el,
      trigger: trigger,
      callback: callback
    };
    this.events.push(evt);
    el.addEventListener(trigger, callback, false);
  }

  resize () {
  }

  destroy () {
    this.events.forEach(evt => {
      evt.el.removeEventListener(evt.trigger, evt.callback);
    });
    this.events = [];
  }

  exists () {
    return this.parts.length > 0;
  }

  reload (source, bin) {
    // if (this.parts.length !== source.paths.length) {
    //   throw new Error('# of input is not correct.');
    // }

    const clearance = getClearanceFromThickness(source.thickness);
    this.binClearance = clearance.bin;
    this.partClearance = clearance.part;

    const sortedOldParts = this.parts.slice();
    const newParts = source.paths.map((path, id) => {
      return this.createPart(id, path);
    });
    const sortedNewParts = this.sort(newParts);
    sortedNewParts.forEach((next, i) => {
      const prev = sortedOldParts[i];
      next.copyTransform(prev);
      this.addPart(next);
      this.removePart(prev);
    });

    // @MEMO
    // - 前回nesting読み込み時から板サイズが変わっている場合は、既存のBinを消去して新しいBinを追加する
    // - 板の縦幅が異なる分、既存のPartの位置をずらす(縦軸は位置の基準点が反転しているため/横軸は影響なし)
    const prev = this.getLastBin();
    const depth = bin.height | bin.depth;
    if (prev.width !== bin.width || prev.depth !== depth) {
      this.updateBinSize(bin.width, depth, source.thickness);
      const oy = depth - prev.depth;
      this.parts.forEach((p) => {
        p.move(0, oy);
      });
    }
  }

  load (source, bin, quantity = 1, nestingData) {
    this.clear();

    const clearance = getClearanceFromThickness(source.thickness);
    this.binClearance = clearance.bin;
    this.partClearance = clearance.part;

    // deep copy source
    this.source = JSON.parse(JSON.stringify(source));

    // sync bin number with that of previously saved nesting data
    if (nestingData !== undefined) {
      for (let i = 0; i < nestingData.bins.length; i++) {
        this.addBin(bin.width, bin.height | bin.depth, source.thickness);
      }
    } else {
      this.addBin(bin.width, bin.height | bin.depth, source.thickness);
    }

    const parts = [];
    source.paths.forEach((path, id) => {
      const count = path.num * quantity;
      for (let i = 0; i < count; i++) {
        try {
          const p = this.addPart(this.createPart(id, path));
          parts.push(p);
        } catch (e) {
          console.warning(e);
        } finally {
          continue;
        }
      }
    });
    this.parts = this.sort(this.parts);

    // move each part to previously recorded position
    if (nestingData !== undefined) {
      for (let part of this.parts) {
        for (let nestingPart of nestingData.parts) {  // previously recorded nesting data
          if (part.source.nesting_id && part.source.nesting_id === nestingPart.id) {
            part.moveTo(nestingPart.position.x, nestingPart.position.y);
          } else {
            continue;
          }
        }
      }
    }

    this.reset();
  }

  restore (json) {
    const { paths } = json;
    if (Array.isArray(paths) && paths.length === this.parts.length) {
      this.parts.forEach((part, index) => {
        const path = paths[index];
        if (path.nesting !== undefined) {
          const { position, rotation } = path.nesting;
          part.position.set(position.x, position.y);
          part.rotation = rotation;
          part.update();
        }
      });
    }
  }

  debug () {
    const path = '24f32ed2-288c-49b3-a790-066f4f27145a.json'; // stool
    // const path = '0e8c7ed4-9b6e-11ea-bedd-abaaaf4d398b.json'
    // const path = '09f45917-9b4e-11ea-93ea-23068db797c9.json'
    return axios.get(`/static/json/${path}`)
      .then((response) => {
        const data = response.data;
        this.load(data);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  getLastBin () {
    if (this.bins.length <= 0) return null;
    return this.bins[this.bins.length - 1];
  }

  addNewBin () {
    const last = this.getLastBin();
    this.addBin(last.width, last.depth, this.thickness);
  }

  removeLastBin () {
    let currentRow = this.row;
    if (this.bins.length > 1) {
      const last = this.bins.pop();
      last.dispose();
    }

    if(!this.previousMoveWasMinusBin){
      this.largestHistoryRow = this.row;  // define the final row number
    }

    // @MEMO
    // currentRow -> old (the row calculated BEFORE the last bin being removed)
    // this.row -> new (the row calculated AFTER the last bin being removed)
    // svgMove - oneRowMove = 2*interval (only correct when moving from row1 to row2)

    this.row = Math.ceil(this.bins.length/rowMax);
    if(this.row != currentRow){ // when an entire row is removed

      // move bins and parts
      let multi = (this.bins.length / rowMax) > 1 ? Math.ceil(this.bins.length / rowMax) : 1;
      this.svg.setAttribute('height', this.initialBinY + multi*(this.bins[0].height | this.bins[0].depth) + offset + interval * 2 * multi);
      let svgMove = (multi - 1) * (this.bins[0].height | this.bins[0].depth) + interval * 2 * multi;

      if(this.row == 1){  // the movement from row1 to row2 is different from others
        this.bins.forEach((bin, i) => {
          bin.move(0, -(oneRowMove+interval*2));
        });
        this.parts.forEach((part, i) => {
          part.move(0, -(oneRowMove+interval*2));
        });
      } else{
        this.bins.forEach((bin, i) => {
          bin.move(0, -oneRowMove);
        });
        this.parts.forEach((part, i) => {
          part.move(0, -oneRowMove);
        });
      }

      // and then move rulers and reference line svgs
      let rulerElements = document.querySelectorAll('.rulerText');
      rulerElements.forEach( element => {
        if(this.row == 1){
          element.style.transform = `translateY(${svgMove - 2*interval}px)`;
        } else{
          element.style.transform = `translateY(${svgMove}px)`;
        }
      });

      let referenceLineElements = document.querySelectorAll('.referenceLine');
      referenceLineElements.forEach( element => {
        if(this.row == 1){
          element.style.transform = `translateY(${svgMove - 2*interval}px)`;
        } else{
          element.style.transform = `translateY(${svgMove}px)`;
        }
      });
    }

    this.previousMoveWasMinusBin = true;  // remember that the last action is "removeBin"
  }

  addBin (width, depth, binThickness) {
    const { binClearance } = this;
    this.thickness = binThickness;

    const last = this.getLastBin();
    const o = new THREE.Vector2(offset + interval, offset + interval);
    const i = this.bins.length;
    const initialValue = offset + interval;

    if (last !== null) {
      // o.set(last.position.x + last.width + interval * 2, last.position.y);
      // o.set(initialValue + (i % rowMax) * (width + interval*2), initialValue + Math.floor((i / rowMax)) * (depth + interval*2)); // for going downward
      o.set(
        initialValue + (i % rowMax) * (width + interval),
        initialValue - Math.floor((i / rowMax)) * (depth + interval)
      ); // for going upward
    }

    const bin = new Bin(this.bins.length, width, depth, binClearance);
    bin.moveTo(o.x, o.y);
    // bin.moveTo(sbpOffset.x, sbpOffset.y);
    bin.addRulers();
    bin.addReferenceLine(width, depth, o.x, o.y, binClearance, binThickness);

    this.svg.appendChild(bin.element);
    this.bins.push(bin);

    // console.log('this is bin: ', bin);
    // console.log('this is grid: ', this.grid);
    // console.log('this is offset: ', offset);
    // bin.position.x = 0;
    // bin.position.y = 0;

    if(this.bins.length == 1){
      this.initialBinY = this.bins[0].position.y;
      oneRowMove = (bin.height | bin.depth) + interval * 2;
    }

    if(this.bins.length <= rowMax){
      this.svg.setAttribute('width', bin.position.x + bin.width + offset);
    }

    const current = Number(this.svg.getAttribute('height'));
    // this.svg.setAttribute('height', Math.max(current, bin.position.y + (bin.height | bin.depth) + offset + interval * 2));
    let multi = (this.bins.length / rowMax) > 1 ? Math.ceil(this.bins.length / rowMax) : 1;

    // this.svg.setAttribute('height', Math.max(current, this.bins[0].position.y + multi*(bin.height | bin.depth) + offset + interval * 2 * multi));
    this.svg.setAttribute('height', this.initialBinY + multi*(bin.height | bin.depth) + offset + interval * 2 * multi);

    // move parts and boards so that the increase seems to go upward
    let svgMove = (multi - 1) * (bin.height | bin.depth) + interval * 2 * multi;

    // @MEMO
    // multi -> new (the latest row calculated by the latest bin)
    // this.row -> old (the row calculated by the bins made before the latest bin)
    // svgMove - oneRowMove = 2*interval (only correct when moving from row1 to row2)

    if (multi > 1) {
      if(multi != this.row){  // when the latest bin needs a new row
        const lastBinIndex = this.bins.length-1;

        this.bins.forEach( (bin, i) => {
          if(multi == 2){  // the movement from row1 to row2 is different from others
            bin.move(0, svgMove);
          } else{
            if(i != lastBinIndex){
              bin.move(0, oneRowMove);  // use oneRowMove for old bins
            } else{
              bin.move(0, svgMove); // use svgMove for the new bin
            }
          }
        });

        this.parts.forEach( (part, i) => {
          if(multi == 2){ // the movement from row1 to row2 is different from others
            part.move(0, svgMove);
          } else{
            part.move(0, oneRowMove);
          }
        });

        this.row = multi;

      } else if(multi == this.row){ // when the latest bin is still on the same row as the previous bin does
        this.bins[this.bins.length-1].move(0, svgMove);
        if(this.previousMoveWasMinusBin){
          this.previousMoveWasMinusBin = false;
        }
      }

      // console.log(this.svg);

      // Object.entries(this.svg.children).forEach( element => {
      //   element[1].style.transform = `translateY(${svgMove}px)`;
      // });

      let rulerElements = document.querySelectorAll('.rulerText');
      rulerElements.forEach( element => {
        element.style.transform = `translateY(${svgMove}px)`;
      });

      let referenceLineElements = document.querySelectorAll('.referenceLine');
      referenceLineElements.forEach( element => {
        element.style.transform = `translateY(${svgMove}px)`;
      });
    } else{
      this.row = multi;
    }

    return bin;
  }

  updateBinSize (width, depth, binThickness) {
    this.clearBins();
    this.addBin(width, depth, binThickness);
  }

  createPart (id, path, rotation) {
    const { partClearance } = this;
    const exPath = path.ex_path;
    const threshold = 1e-2;
    const simplified = this.simplify(exPath, threshold);
    let inner = [];
    if (path.in_paths.length > 0) {
      inner = path.in_paths.map(p => this.simplify(p, threshold));
    }

    return new Part(id, simplified, inner, path, partClearance, rotation);
  }

  addPart (part, position, undoRedo) {
    this.svg.appendChild(part.element);
    this.parts.push(part);

    part.on('select', this.onSelect.bind(this));
    part.on('rotate', this.onRotate.bind(this));
    // part.addEvent('unselect', this.onSelect.bind(this))
    // part.moveTo(offset + interval + binClearance, offset + interval + binClearance);
    if (position != undefined) {
      const copyOffset = 100;
      part.moveTo(position.x, position.y - copyOffset);
    }
    this.unselectNodes();

    if(!undoRedo){ // DO NOT push another history for undo/redo. Pushing another history causes disorder
      this.pushHistory(new TransformOperation([part]));
    }
  }

  addParts (parts, undoRedo) {
    let newParts = [];
    for(let part of parts){
      let newId = this.parts.length;
      let newPart = this.createPart(newId, part.source, part.rotation);
      this.svg.appendChild(newPart.element);
      this.parts.push(newPart);

      newPart.on('select', this.onSelect.bind(this));
      newPart.on('rotate', this.onRotate.bind(this));

      if (part.position != undefined) {
        const copyOffset = 100; // make copied part a bit higher than the original part so that users can recognize
        newPart.moveTo(part.position.x, part.position.y - copyOffset);
      }

      newParts.push(newPart);
    }

    this.unselectNodes(); // unselect all first
    this.selectNodes(newParts); // keep newly copied parts selected

    if(!undoRedo){ // DO NOT push another history for undo/redo. Pushing another history causes disorder
      this.pushHistory(new CopyOperation(newParts));
    }
  }

  removePart (part, undoRedo) {
    const index = this.parts.indexOf(part);
    if (index >= 0) {
      this.parts.splice(index, 1);
    }

    if(!undoRedo){ // DO NOT push another history for undo/redo. Pushing another history causes disorder
      this.pushHistory(new RemoveOperation([part]));
    }

    part.dispose();
    this.unselectNodes();
  }

  removeParts (parts, undoRedo) {
    if(!undoRedo){ // DO NOT push another history for undo/redo. Pushing another history causes disorder
      this.pushHistory(new RemoveOperation(parts));
    }

    for(let part of parts){
      const index = this.parts.indexOf(part);
      if (index >= 0) {
        this.parts.splice(index, 1);
      }

      part.dispose();
      this.unselectNodes();
    }
  }

  sort (parts) {
    return parts.sort((p0, p1) => {
      return (Math.abs(p0.area()) > Math.abs(p1.area())) ? -1 : 1;
    });
  }

  nestByApi (result) {
    const { binClearance } = this;
    const { placements, placed, bins } = result;

    for (let i = bins.length - this.bins.length; i > 0; i--) {
      this.addNewBin();
    }

    const origin = new THREE.Vector2();

    const { depth: height } = this.getLastBin();
    placements.forEach(placement => {
      const { bin, part: index, position, rotation } = placement;
      const ox = this.bins[bin].position.x;
      const oy = this.bins[bin].position.y;
      const found = this.parts.find(p => p.id === index);
      if (found !== undefined) {
        // CAUTION: invert y position to convert nesting coordinates
        // eslint-disable-next-line camelcase
        const { ex_path } = found.source;
        let maxY = Number.MIN_SAFE_INTEGER;
        ex_path.map(p => {
          maxY = Math.max(maxY, p.y);
        });

        const center = found.center(found.points);
        const displacement = new THREE.Vector2(position.x, position.y);
        displacement.rotateAround(origin, -rotation);

        const px = displacement.x;
        const py = -displacement.y - maxY;
        const movement = new THREE.Vector2(px, py);

        movement.rotateAround(center.clone().negate(), -rotation);
        movement.y += height;

        found.moveTo(
          ox + binClearance + movement.x,
          oy - binClearance + movement.y
        );
        found.rotateTo(-rotation);
      }
    });

    this.updateIntersections();
    this.pushHistory(new TransformOperation(this.parts));
  }

  nestByRect () {
    // CAUTION: Already sorted in load()
    const { binClearance, partClearance } = this;

    //const clearance = partClearance + binClearance;
    const clearance = partClearance + (binClearance * 2); //emarf_cam のチェック方法に合わせる (sheet_Clearance * 2)    
    const boxes = this.parts.map((p, idx) => {
      const bb = p.bounds(false);
      const w = bb.width;    
      return {
        id: idx,
        width: w + clearance + partClearance,
        height: bb.height + clearance + partClearance,        
        offset: {
          x: bb.x - clearance,
          y: bb.y - clearance
        }
      };
    });

    const head = this.bins[0];

    const sources = boxes.slice();
    const results = [];
    let index = 0;
    const width = head.width;
    const height = head.height | head.depth;
    let sprite = new ShelfPack(width, height, { autoResize: false });

    while (sources.length > 0) {
      let latest = sources[0];
      // console.log('Total 部材 size:', latest.width, latest.height)
      // console.log('板 size:', width, height)
      if (latest.width >= width || latest.height >= height) break; // はみ出る部材があればpackingしない

      let result = sprite.packOne(latest.width, latest.height, latest.id);
      if (result) {
        sources.splice(0, 1);
        results.push({
          index: index,
          bin: result
        });
      } else {        
        if (sprite.bins.length <= 0) {
          // CAUTION
          // in case of a part that doesn't fit in the bin          
          break;
        }
        // create new shelf
        index++;
        sprite = new ShelfPack(width, height, { autoResize: false });
      }      
    }
    
    const ox = head.position.x;
    const oy = head.position.y;

    results.forEach(result => {
      if (result.index >= this.bins.length) {
        this.addNewBin();
      }
      const bin = result.bin;
      const part = this.parts[bin.id];
      const box = boxes[bin.id];
      part.rotateTo(0);

      // for numerical error in intersection
      const rx = Math.random();
      const ry = Math.random();

      const dx = (head.width + interval) * (result.index % rowMax) + 1;
      const y = height - bin.y - box.height + partClearance - 2;
      const cy = Math.floor(result.index / rowMax) == 0 ? 0 : (Math.floor(result.index / rowMax) - 1) * 50 + 150 -1;  // cy is used to compensate the y offset miss
      part.moveTo(bin.x - box.offset.x + ox + rx + dx, y + oy + ry + cy);
      part.keep();
    });

    // parts that doesn't fit in the bin
    sources.forEach(box => {
      const part = this.parts[box.id];
      part.rotateTo(0);
      part.moveTo(-box.offset.x + ox, -box.offset.y + oy);
      part.keep();
    });

    this.updateIntersections();
  }

  validate () {
    const hasIntersected = this.hasIntersectedPart();
    return !hasIntersected;
  }

  build () {
    this.source.paths = this.parts.map(part => {
      const idx = this.bins.findIndex(b => b.containsPart(part));
      if (idx < 0) {
        console.warn('bin not found');
        return {};
      }
      const bin = this.bins[idx];
      const path = part.build(bin);
      path.sheet = idx;
      path.num = 1;
      path.nesting = {
        position: part.position,
        rotation: part.rotation
      };
      return path;
    });

    this.source.bins = this.bins.map(b => {
      return {
        width: b.width,
        depth: b.depth
      };
    });
    this.source.nesting = false;
    return this.source;
  }

  serialize () {
    return {
      bins: this.bins.map(b => b.serialize()),
      parts: this.parts.map(p => p.serialize())
    };
  }

  deserialize (source) {
    const { bins, parts } = source;
    if (bins === undefined || parts === undefined) return;
    if (bins.length !== this.bins.length || parts.length !== this.parts.length) return;

    parts.forEach((p, i) => {
      this.parts[i].deserialize(p);
    });
  }

  clearBins () {
    this.bins.forEach(b => b.dispose());
    this.bins = [];
  }

  clear () {
    this.clearBins();
    this.parts.forEach(p => p.dispose());
    this.parts = [];

    this.clearHistory();
    this.scale = 1;
    this.moveTo(-offset, -offset);
  }

  reset (nesting = true) {
    this.clearHistory();
    this.scale = 1;

    if (this.bins.length > 0) {
      const bin = this.bins[0];
      // this.moveTo(-offset, -bin.depth + offset * 0.25);
      this.moveTo(-offset, -bin.depth * 0.4 - offset);  // when offset is 1500
    } else {
      this.moveTo(-offset, -offset);
    }

    if (nesting) { this.nestByRect(); }
  }

  redo () {
    if (this.current >= this.history.length) return;

    switch (this.history[this.current].opType()) {
      case 'remove':
        for(let part of this.history[this.current].getRootParts()){
          this.removePart(part, true);
        }
        break;
      case 'copy':
        for(let part of this.history[this.current].getRootParts()){
          const position = undefined;
          this.addPart(part, position, true);
        }
        break;
      default:  // for transform
        this.history[this.current].redo(this);
    }

    this.current++;
  }

  undo () {
    if (this.current <= 0) return;
    this.current--;

    switch (this.history[this.current].opType()) {
      case 'remove':
        for(let part of this.history[this.current].getRootParts()){
          const position = undefined;
          this.addPart(part, position, true);
        }
        break;
      case 'copy':
        for(let part of this.history[this.current].getRootParts()){
          this.removePart(part, true);
        }
        break;
      default:  // for transform
        this.history[this.current].undo(this);
    }
  }

  pushHistory (op) {
    let len = this.history.length;
    if (this.current < len) {
      this.history.splice(this.current, len - this.current);
    }
    this.history.push(op);
    this.current++;

    const limit = 15;
    if (this.history.length > limit) {
      this.history.splice(0, 1);
      this.current--;
    }

    return op;
  }

  clearHistory () {
    this.history = [];
    this.current = 0;
  }

  trackKey (keyCode) {
    this.keyCode = keyCode;
    this.keys[keyCode] = true;
  }

  clearKey (keyCode) {
    this.keys[this.keyCode] = this.keys[keyCode] = false;
    this.keyCode = -1;
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/nesting/nesting.js